import { useState, useMemo } from "react";
import { Share2, Loader2, Download } from "lucide-react";
import { Button } from "../ui/button";
import { useEvents } from "../../context/EventContext";
import { useBalances } from "../../hooks/useBalances";
import { useAllTransfers } from "../../hooks/useTransfers";
import {
  generateSettlementPDF,
  generateFilename,
} from "../../lib/pdf/settlement-pdf";
import { canShareFiles, shareContent, downloadFile } from "../../lib/share";

export function ShareButton() {
  const { currentEvent } = useEvents();
  const [isGenerating, setIsGenerating] = useState(false);

  const { groupBalances, unassignedBalances, totalSpent } =
    useBalances(currentEvent);
  const transfers = useAllTransfers(groupBalances, unassignedBalances);

  const supportsFileSharing = useMemo(() => canShareFiles(), []);

  const perPerson = useMemo(() => {
    if (!currentEvent || currentEvent.participants.length === 0) return 0;
    return Math.round(totalSpent / currentEvent.participants.length);
  }, [currentEvent, totalSpent]);

  // Don't render if no event or no expenses
  if (!currentEvent || currentEvent.expenses.length === 0) {
    return null;
  }

  const handleShare = async () => {
    if (!currentEvent) return;

    setIsGenerating(true);

    try {
      const pdfBlob = generateSettlementPDF({
        event: currentEvent,
        totalSpent,
        perPerson,
        transfers,
      });

      const filename = generateFilename(currentEvent.name);

      if (supportsFileSharing) {
        const file = new File([pdfBlob], filename, { type: "application/pdf" });
        await shareContent({
          title: `Balance - ${currentEvent.name}`,
          files: [file],
        });
      } else {
        // Fallback: download the file
        downloadFile(pdfBlob, filename);
      }
    } catch (err) {
      console.error("Share failed:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="shrink-0"
      onClick={handleShare}
      disabled={isGenerating}
      aria-label={
        supportsFileSharing ? "Compartir balance" : "Descargar balance"
      }
    >
      {isGenerating ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : supportsFileSharing ? (
        <Share2 className="h-5 w-5" />
      ) : (
        <Download className="h-5 w-5" />
      )}
    </Button>
  );
}
